/**
 * 广告命名模板：单个字段的只读摘要卡。
 *
 * PR-AT-20260626-004 第 5 条：进入页面默认展示摘要而非编辑器。
 *   - 1 组枚举 → 1 行
 *   - 2 组枚举 → 2 行
 *   - 输入框    → "输入框"
 *   - 枚举项以 "、" 分隔；灰色底 + 紧凑行高
 *   - 右侧操作：上移 / 下移 / 编辑 / 删除
 */

import type { TemplateField } from "../../types/adNameTemplate.types";

interface Props {
  index: number;
  field: TemplateField;
  onEdit: () => void;
  onDelete: () => void;
  onMoveUp: () => void;
  onMoveDown: () => void;
  canMoveUp: boolean;
  canMoveDown: boolean;
  /** 只读模式：禁用上移/下移/编辑/删除（当前用户无该页编辑权限时置 true） */
  readOnly?: boolean;
}

export function FieldSummaryCard({
  index,
  field,
  onEdit,
  onDelete,
  onMoveUp,
  onMoveDown,
  canMoveUp,
  canMoveDown,
  readOnly = false,
}: Props) {
  return (
    <div className="leo-card rounded-xl px-4 py-3">
      <div className="flex items-start gap-3">
        <div className="grid h-7 w-7 shrink-0 place-items-center rounded-lg bg-accent-cta/15 font-display text-[12.5px] font-semibold text-accent-cta-hover">
          {index + 1}
        </div>
        <div className="flex-1 space-y-1.5">
          <div className="text-[13px] font-semibold text-text-emphasis">
            {field.name || <span className="text-text-muted-weak">（未命名字段）</span>}
          </div>
          <div className="space-y-1">
            {field.combos.map((c, i) => (
              <SummaryLine key={i} index={i} comboType={c.combo_type}
                items={c.combo_type === "SINGLE_ENUM" ? (c.single_enum_combo?.enum_items ?? []) : null}
                hintText={c.combo_type === "INPUT_BOX" ? (c.input_box_combo?.hint_text ?? null) : null}
              />
            ))}
          </div>
          {field.combos.length >= 2 ? (
            <div className="text-[10.5px] text-text-muted-weak">
              与下一字段分隔：<span className="text-text-muted">{field.separator_to_next || "（无）"}</span>
            </div>
          ) : null}
        </div>
        <div className="flex shrink-0 items-center gap-1">
          <button
            type="button"
            onClick={onMoveUp}
            disabled={!canMoveUp || readOnly}
            className="leo-btn-ghost px-2 py-1.5 text-[12px]"
            title="上移"
          >
            ↑
          </button>
          <button
            type="button"
            onClick={onMoveDown}
            disabled={!canMoveDown || readOnly}
            className="leo-btn-ghost px-2 py-1.5 text-[12px]"
            title="下移"
          >
            ↓
          </button>
          <button
            type="button"
            onClick={onEdit}
            disabled={readOnly}
            className="leo-btn-ghost px-2.5 py-1.5 text-[12px]"
            title="编辑"
          >
            编辑
          </button>
          <button
            type="button"
            onClick={onDelete}
            disabled={readOnly}
            className="leo-btn-ghost is-danger px-2 py-1.5 text-[12px]"
            title="删除"
          >
            ✕
          </button>
        </div>
      </div>
    </div>
  );
}

interface SummaryLineProps {
  index: number;
  comboType: "SINGLE_ENUM" | "INPUT_BOX" | string;
  items: Array<{ item_code: string }> | null;
  hintText: string | null;
}

function SummaryLine({ index, comboType, items, hintText }: SummaryLineProps) {
  const label = comboType === "INPUT_BOX" ? "输入框" : `枚举组 ${index + 1}`;
  if (comboType === "INPUT_BOX") {
    return (
      <div className="flex items-center gap-2">
        <span className="text-[10.5px] uppercase tracking-[0.06em] text-text-muted-weak w-[68px] shrink-0">
          {label}
        </span>
        <span className="rounded-md bg-da-inset px-2 py-0.5 text-[12px] text-text-strong">
          {hintText?.trim() ? hintText : "输入框"}
        </span>
      </div>
    );
  }
  const codes = (items ?? []).map((it) => it.item_code).filter(Boolean);
  return (
    <div className="flex items-center gap-2">
      <span className="text-[10.5px] uppercase tracking-[0.06em] text-text-muted-weak w-[68px] shrink-0">
        {label}
      </span>
      <span
        className="rounded-md bg-da-inset px-2 py-0.5 text-[12px] text-text-strong break-all"
        title={codes.join("、") || "（无枚举项）"}
      >
        {codes.length > 0 ? codes.join("、") : "（无枚举项）"}
      </span>
    </div>
  );
}
